import { AutoComplete, Col, Input, PageHeader, Row, Tag, Tree } from 'antd';
import { Link, useHistory } from 'react-router-dom';
import React, { ReactElement, useEffect, useState } from 'react';

import { DownOutlined } from '@ant-design/icons';
import useFetch from '../hooks/fetcher';

const API_SERVICE_BASE_URL = process.env.REACT_APP_API_SERVICE_BASE_URL || '';

type TreeDataType = {
    title: any;
    key: string;
    children: {
        title: any;
        key: string;
        recipes_id: string;
    }[];
}[];

type AllRecipesResponseType = {
    recipes: {
        recipes_id: string;
        pinyin: string;
        english_name: string;
        chinese_character: string;
        category: string;
    }[];
};

type recipeIdType = {
    recipes_id: string;
};

type countType = {
    count: string;
};

type pinyinType = {
    pinyin: string;
} & recipeIdType;

type chineseCharacterType = {
    chinese_character: string;
} & recipeIdType;
type englishNameType = {
    english_name: string;
} & recipeIdType;

type fetchResultType = {
    pinyin: { data: pinyinType[] } & countType;
    chinese_character: { data: chineseCharacterType[] } & countType;
    english_name: { data: englishNameType[] } & countType;
};

type optionType = {
    label: ReactElement;
    options: {
        value: string;
        label: ReactElement;
    }[];
};

const renderCategoryTitle = (title: string, count: string): ReactElement => (
    <div>
        <span style={{ marginRight: 8 }}>{title}</span>

        <span style={{ color: '#1890ff' }}>{count}</span>
    </div>
);

const renderCategoryItem = (
    pinyinName: string,
    englishName: string,
    chineseChar: string,
    recipeId: string,
): ReactElement => (
    <Link to={`/recipe/detail/${englishName}/${recipeId}`}>
        <div>
            <Tag style={{ marginRight: 8 }}>{chineseChar}</Tag>
            <span style={{ marginRight: 8 }}>{englishName}</span>
            <span style={{ marginRight: 8 }}>-</span>
            <span style={{ marginRight: 8 }}>{pinyinName}</span>
        </div>
    </Link>
);

const renderTitle = (title: string, count: string, sname: string): ReactElement => (
    <span>
        {title}
        <Link style={{ float: 'right' }} to={`recipe/detail/${sname}/Working_in_progress`}>
            show all {count} results
        </Link>
    </span>
);

const divider = '%%';

const renderItem = (
    searchName: string,
    recipeId: string,
    key: number,
): { value: string; key: string; label: ReactElement } => ({
    value: searchName,
    key: searchName + divider + recipeId + divider + key,
    label: (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
            }}
        >
            {searchName}
            <span>-</span>
        </div>
    ),
});

const transformToTreeData = (data: AllRecipesResponseType) => {
    const treeData: TreeDataType = [];

    const categoriesIndex: any = {};

    data.recipes.map((item) => {
        if (!categoriesIndex[item.category]) {
            categoriesIndex[item.category] = treeData.length;

            treeData.push({
                title: item.category,
                key: '' + treeData.length,
                children: [],
            });
        }
        treeData[categoriesIndex[item.category]].children.push({
            title: renderCategoryItem(item.pinyin, item.english_name, item.chinese_character, item.recipes_id),
            key: categoriesIndex[item.category] + '-' + item.recipes_id,
            recipes_id: item.recipes_id,
        });
    });

    console.log(categoriesIndex, treeData);

    return treeData.map((item) => {
        item.title = renderCategoryTitle(item.title, '' + item.children.length);
        return item;
    });
};

const updateOptions = (data: fetchResultType, keyword: string): optionType[] => {
    return [
        {
            label: renderTitle('Chinese Character', data.chinese_character.count, keyword),
            options: data.chinese_character.data.map((e, i) => renderItem(e.chinese_character, e.recipes_id, i)),
        },
        {
            label: renderTitle('Pinyin', data.pinyin.count, keyword),
            options: data.pinyin.data.map((e, i) => renderItem(e.pinyin, e.recipes_id, i)),
        },
        {
            label: renderTitle('English Name', data.english_name.count, keyword),
            options: data.english_name.data.map((e, i) => renderItem(e.english_name, e.recipes_id, i)),
        },
    ];
};

const updateResultCount = (data: fetchResultType): string =>
    (
        parseInt(data.chinese_character.count) +
        parseInt(data.pinyin.count) +
        parseInt(data.english_name.count)
    ).toString();

const PageRecipeSearch: React.FC = () => {
    const { get } = useFetch(API_SERVICE_BASE_URL);
    const [options, setOptions] = useState<optionType[]>([]);
    const [resultCount, setresultCount] = useState<string>('');
    const [allRecipes, setAllRecipes] = useState<AllRecipesResponseType>();
    const history = useHistory();
    const gutterH = 16;
    const gutterV = 16;
    const colHelperProps = {
        xs: 0,
        sm: 0,
        md: 2,
        lg: 5,
        xl: 7,
    };
    const colContentProps = {
        xs: 24,
        sm: 24,
        md: 20,
        lg: 14,
        xl: 10,
    };

    useEffect(() => {
        get('/api/recipe_idx_all.php', {}).then((r) => {
            setAllRecipes(r);
            if (r) {
                transformToTreeData(r);
            }
        });
    }, []);

    // choose one of the results
    const onSelect = (e: string, opt: any): void => {
        const [searchName, recipeId] = opt.key.split(divider);
        history.push(`recipe/detail/${searchName}/${recipeId}`);
    };

    // onChange event
    const onSearch = (e: string): Promise<void> =>
        get('/api/recipe_idx.php', { name: e }).then((r: fetchResultType) => {
            setOptions(updateOptions(r, e));
            const c = updateResultCount(r);
            setresultCount(e === '' ? '' : c + ' results. Press enter to show them all.');
        });

    return (
        <div>
            <PageHeader title="Recipe search" style={{ marginBottom: '10vh' }} />
            <Row justify="space-between" gutter={[gutterH, gutterV]}>
                <Col {...colHelperProps}></Col>
                <Col {...colContentProps} style={{ display: 'flex' }}>
                    <AutoComplete style={{ flex: 1 }} options={options} onSelect={onSelect} onSearch={onSearch}>
                        <Input.Search size="large" placeholder="Search recipe" />
                    </AutoComplete>
                </Col>
                <Col {...colHelperProps}>{resultCount}</Col>
            </Row>
            <Row gutter={[gutterH, gutterV]}>
                <Col {...colHelperProps}></Col>
                <Col {...colContentProps}>
                    <strong>Examples:</strong>
                    <br />
                    Chinese characters:<strong> 半夏厚朴汤</strong>
                    <br />
                    Transliteration (Pinyin):<strong> Ban Xia Hou Po Tang</strong>
                    <br />
                    Principle Formula (English name of the recipe/ TCM disease):
                    <strong> Pinellia Decoction To Drain The Epigastrium</strong>
                </Col>
            </Row>
            <br />
            <Row gutter={[gutterH, gutterV]}>
                <Col {...colHelperProps}></Col>
                <Col {...colContentProps}>
                    <div>
                        <strong>Categories search of recipes/ TCM diseases:</strong>
                    </div>
                    <Tree
                        // showIcon
                        // defaultExpandAll
                        defaultSelectedKeys={['0-0-0']}
                        switcherIcon={<DownOutlined />}
                        // loadData={onLoadData}
                        treeData={allRecipes ? transformToTreeData(allRecipes) : []}
                    />
                </Col>
            </Row>
        </div>
    );
};

export default PageRecipeSearch;
